import type { ParameterDefinitionCreateOperation } from '@festina/configuration-general-types/configuration'
import { dateUtils, notEmpty, type Optional } from '@festina-life/core'
import {
  FormikDatePicker,
  FormikForm,
  FormikSelect,
} from '@festina-life/ui/formikForms'
import { Checkbox } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import dayjs from 'dayjs'
import { useCallback, useRef, useState } from 'react'

import { useConfigurationContext } from '../../context/configurationContext'
import { stringToDateTimeNullSafe } from '../../uiModels/helpers'
import { getParameterDefinitionAtDate } from '../../uiModels/parameterOperations/helpers'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import { useOperationsContext } from '../../workspace/operationsContext'
import { DraftCreationFormId } from '../draftCreationModal/DraftCreationModal'
import { draftCreationStatusReporter } from '../draftCreationModal/draftCreationStatusReporter'
import type {
  DraftCreatedResult,
  DraftCreationProps,
} from '../draftCreationModal/types'
import { DraftPathSelector } from '../draftLocationSelectors/DraftPathSelector'
import { useIsViewDateActiveForOperation } from '../moveViewDate/useIsViewDateActiveForOperation'
import { validateKey } from './helpers'
import { ParameterDraftTypeInputs } from './ParameterDraftTypeInputs'
import type { CreateParameterFormData } from './types'
import { useParameterDefinitionDraftValidationSchema } from './useParameterDefinitionDraftValidationSchema'

const MoveViewAtInputId = 'moveViewAtDate'

export const ParameterDraftCreationForm = ({
  onStatusChanged,
  initialValue,
  layer,
  block,
}: DraftCreationProps<'parameterDefinition'> & {
  initialPackagePath?: string[]
}) => {
  const translate = useTranslate()
  const { onAddOrUpdateOperation } = useOperationsContext()
  const { applicationParameterDefinitions } =
    useApplicationViewOperationsContext()
  const { onViewAtChanged, viewAt } = useConfigurationContext()
  const previousParameterNameRef = useRef<string>(null)

  const initialPackagePath = initialValue?.value?.packagePath ?? []

  const formikInitialValues = {
    parameterId: initialValue?.value?.parameterId ?? '',
    fromDate: initialValue?.value?.fromDate
      ? dateUtils.dateAsServerStr(initialValue.value.fromDate)
      : undefined,
    environmentType: null,
    parameterType: null,
    packagePath: initialPackagePath,
  } satisfies CreateParameterFormData

  const copiedParameter = initialValue?.value?.copyParameter
  const isCreatingNewParameter = !copiedParameter
  const validationSchema = useParameterDefinitionDraftValidationSchema({
    layer,
    block,
  })

  const checkIsViewDateActiveForParameterOperation =
    useIsViewDateActiveForOperation<ParameterDefinitionCreateOperation>({
      operationInterval: (operation) => ({
        from: stringToDateTimeNullSafe(
          operation.entity?.fromDate,
          Number.NEGATIVE_INFINITY,
        ),
      }),
    })

  const getOperationsForParameterId = useCallback(
    (parameterId: Optional<string>) =>
      parameterId
        ? applicationParameterDefinitions
            ?.get(parameterId)
            ?.operations.map(({ internalOperation }) => internalOperation)
        : undefined,
    [applicationParameterDefinitions],
  )

  const [shouldMoveDate, setShouldMoveDate] = useState(
    !checkIsViewDateActiveForParameterOperation(
      dateUtils.dateAsServerStr(initialValue?.value?.fromDate),
      getOperationsForParameterId(initialValue?.value?.copyParameter?.name),
    ),
  )

  const copiedParameterDefinitionOperations = initialValue?.value?.parameterId
    ? applicationParameterDefinitions?.get(initialValue.value.parameterId)
    : undefined

  const copiedParameterDefinitionAtDate = copiedParameterDefinitionOperations
    ? getParameterDefinitionAtDate(copiedParameterDefinitionOperations, viewAt)
    : undefined

  const handleSubmitForm = async (
    input: CreateParameterFormData,
  ): Promise<DraftCreatedResult> => {
    const parameterDefinitionsForIdInsideSelectedLayer =
      applicationParameterDefinitions?.get(input.parameterId)

    const shadowingParameterDefinitionEntity = !copiedParameterDefinitionAtDate
      ? getParameterDefinitionAtDate(
          parameterDefinitionsForIdInsideSelectedLayer,
          input.fromDate
            ? dateUtils.dateUtcAsLocal(input.fromDate).toDate()
            : null,
        )?.internalOperation.entity
      : undefined

    const name = input.parameterId
    const fromDate = input.fromDate
      ? dateUtils.dateAsServerStr(input.fromDate)
      : null

    const parameterDefinitionOperation: ParameterDefinitionCreateOperation = {
      _type: 'ParameterDefinitionCreateOperation',
      entity: {
        _type: 'ParameterDefinitionInOut',
        ...(copiedParameterDefinitionAtDate?.internalOperation.entity ??
          shadowingParameterDefinitionEntity ??
          {}),
        name,
        fromDate,
        parameterType: input.parameterType,
        environmentType: input.environmentType,
        packageName: input.packagePath.join('.'),
      },
    }

    const createdOnWorkspaceKey = await onAddOrUpdateOperation({
      operation: parameterDefinitionOperation,
      layer,
      block,
      shouldOpenUnifiedBranchWorkspace: true,
    })
    const allOperationsForId = getOperationsForParameterId(input.parameterId)
    const isParameterDateActive = checkIsViewDateActiveForParameterOperation(
      fromDate,
      allOperationsForId,
    )

    if (!isParameterDateActive && shouldMoveDate) {
      const firstNonEmptyOperationDate = allOperationsForId
        ?.map((operation) => operation.entity?.fromDate)
        .filter(notEmpty)
        .at(0)

      onViewAtChanged(
        fromDate
          ? dateUtils.dateUtcAsLocal(fromDate).toDate()
          : // ! Given that if there's a fromDate and the param is not active then there must be a version with a fromDate in the middle of the beginning of time and the viewAt date
            dayjs(firstNonEmptyOperationDate!).subtract(1, 'day').toDate(),
      )
    }

    return {
      createdDraft: {
        name,
        fromDate: fromDate
          ? dateUtils.dateUtcAsLocal(fromDate).toDate()
          : undefined,
        type: 'parameterDefinition',
        layer,
        block,
      },
      addedToWorkspaceKey: createdOnWorkspaceKey,
    }
  }

  return (
    <FormikForm
      initialValues={formikInitialValues}
      validationSchema={validationSchema}
      formId={DraftCreationFormId}
      onSubmit={draftCreationStatusReporter(onStatusChanged, handleSubmitForm)}
    >
      {({ values, setFieldValue, resetForm, touched }) => {
        const matchingParameter = applicationParameterDefinitions?.get(
          values.parameterId,
        )

        const matchingParameterAtDate = matchingParameter
          ? getParameterDefinitionAtDate(
              matchingParameter,
              values.fromDate
                ? dateUtils.dateUtcAsLocal(values.fromDate).toDate()
                : null,
            )
          : undefined

        const isExistentParameter = !!matchingParameterAtDate

        if (previousParameterNameRef.current !== values.parameterId) {
          previousParameterNameRef.current = values.parameterId
          resetForm({
            values: { ...values, parameterId: values.parameterId },
            touched: {
              ...touched,
              parameterType: false,
              environmentType: false,
            },
          })
        }

        if (matchingParameterAtDate) {
          if (
            values.parameterType !==
            matchingParameterAtDate.internalOperation.entity?.parameterType
          ) {
            void setFieldValue(
              validateKey('parameterType'),
              matchingParameterAtDate.internalOperation.entity?.parameterType,
            )
          }

          if (
            values.environmentType !==
            matchingParameterAtDate.internalOperation.entity?.environmentType
          ) {
            void setFieldValue(
              validateKey('environmentType'),
              matchingParameterAtDate.internalOperation.entity?.environmentType,
            )
          }
        }

        const isParameterDateActive =
          checkIsViewDateActiveForParameterOperation(
            values.fromDate,
            getOperationsForParameterId(values.parameterId),
          )

        return (
          <>
            <FormikSelect
              label={translate('parameterDraftForm.parameterId.label')}
              name={validateKey('parameterId')}
              options={Array.from(
                (applicationParameterDefinitions?.keys().toArray() ?? []).sort(
                  (parameterA, parameterB) =>
                    parameterA.localeCompare(parameterB, undefined, {
                      sensitivity: 'base',
                    }),
                ),
              )}
              disabled={!isCreatingNewParameter}
              isValueCreator
              valueFromString={(value) => value}
            />
            <FormikDatePicker
              name={validateKey('fromDate')}
              label={translate('parameterDraftForm.fromDate.label')}
            />
            <ParameterDraftTypeInputs
              layer={layer}
              isDisabled={isExistentParameter}
            />
            {!isParameterDateActive && (
              <div className="mt-4">
                <Checkbox
                  wrapperProps={{
                    label: translate('templateDraftForm.moveViewAtLabel'),
                    description: translate(
                      'templateDraftForm.moveViewAtDescription',
                    ),
                  }}
                  id={MoveViewAtInputId}
                  checked={shouldMoveDate}
                  onChange={setShouldMoveDate}
                />
              </div>
            )}
            <DraftPathSelector layer={layer} block={block} />
          </>
        )
      }}
    </FormikForm>
  )
}
